import React from 'react';

import { TextField } from 'Containers/App/types';
import { ContentfulTextNoteType } from 'ContentfulDefaults/types/_shared';
import { useTextContext } from 'Contexts/textContext';
import { TextNote } from 'DesignLibrary/molecules/TextNote';
import RichText from 'Shared/RichText';

export interface ContentfulTextNoteProps {
  note?: ContentfulTextNoteType;
  sectionKey?:
    | 'unified_landing_page'
    | 'health_section'
    | 'review_section'
    | 'plan_customizations'
    | 'benefits_section_additional'
    | 'profile_section.member'
    | 'profile_section.medicare';
  sectionIndex?: number;
  className?: string;
  'data-testid'?: string;
}

export const ContentfulTextNote = ({
  note,
  sectionKey,
  sectionIndex = -1,
  className = '',
  'data-testid': dataTestId,
}: ContentfulTextNoteProps) => {
  let sectionPrefix = sectionKey as string;
  if (sectionIndex > -1) {
    sectionPrefix = `${sectionKey}[${sectionIndex}]`;
  }

  const { retrieveContentfulData } = useTextContext();

  const hasShortText = note
    ? !!note.short_text
    : Boolean(retrieveContentfulData<string>(`${sectionPrefix}.text_note.short_text`));

  const hasExpandedText = note
    ? !!note.long_text
    : Boolean(retrieveContentfulData<string>(`${sectionPrefix}.text_note.long_text`));

  const buttonText = note
    ? note.button_text
    : retrieveContentfulData<string>(`${sectionPrefix}.text_note.button_text`);

  if (!hasShortText) {
    return null;
  }

  // Dynamically determine props based on whether note exists
  const shortTextProps = note
    ? { fieldContent: note.short_text ?? undefined, field: undefined }
    : { field: `${sectionPrefix}.text_note.short_text` as TextField };

  const longTextProps = note
    ? { fieldContent: note.long_text ?? undefined, field: undefined }
    : { field: `${sectionPrefix}.text_note.long_text` as TextField };

  return (
    <TextNote
      className={className}
      buttonText={buttonText}
      shortDisplayText={
        <RichText
          {...shortTextProps}
          inlineParagraphs={false}
          useStylingWrapper
          richTextStyling={{ p: { size: 'small', color: 'var(--text-black)' } }}
        />
      }
      hasExpandedText={hasExpandedText}
      expandedText={
        <RichText
          {...longTextProps}
          richTextStyling={{ p: { size: 'small', color: 'var(--text-black)' } }}
          inlineParagraphs={false}
          useStylingWrapper
          maxHeaderSize={2}
        />
      }
      data-testid={dataTestId}
    />
  );
};
